import { trapFocus, removeTrapFocus } from '@theme/focus';
import { debounce, isClickedOutside, prefersReducedMotion } from '@theme/utilities';

/**
 * <gamba-search-panel> — el panel de busqueda de la nav.
 *
 * Estados: idle (atajos + la frase de Oli tipeada), resultados y vacio.
 * El markup lo emite snippets/gamba-search-panel.liquid y el boton que lo abre
 * vive en sections/gamba-header.liquid con [data-search-toggle].
 *
 * POR QUE NO EXTIENDE `Component` (@theme/component)
 * -------------------------------------------------
 * `Component` monta un MutationObserver con `subtree: true` para mantener sus
 * `refs`, y en este proyecto ese costo ya se descarto una vez (ver
 * docs/estado-ejecucion.md, correccion 32). Aca ademas el JS reescribe el
 * subarbol de resultados en cada tecleo, o sea que seria el peor caso: cada
 * render dispararia el observer. Las cuatro referencias se resuelven una vez.
 *
 * QUE SI SE REUSA DE HORIZON
 * --------------------------
 * - `trapFocus` / `removeTrapFocus` de @theme/focus. Importa que sea el MISMO
 *   modulo que use el resto del theme: los handlers viven en un objeto de
 *   modulo, asi que dos trampas nunca quedan pisadas a la vez.
 * - `debounce` (trae `.cancel()`), `isClickedOutside` y `prefersReducedMotion`
 *   de @theme/utilities.
 * - El patron de `AbortController` que cancela la request anterior, calcado de
 *   `assets/predictive-search.js`.
 *
 * QUE NO SE REUSA, Y POR QUE
 * --------------------------
 * - `DialogComponent` (@theme/dialog) envuelve un `<dialog>` nativo y lo abre
 *   con `showModal()`: eso es un modal centrado con backdrop y scroll lock. El
 *   buscador Gamba es un dropdown anclado a la nav, asi que el elemento
 *   equivocado.
 * - La busqueda predictiva de Horizon (`sections/predictive-search.liquid` via
 *   Section Rendering API) devuelve tarjetas de producto, carruseles de
 *   coleccion y pills de sugerencia, con su propia hoja de estilos. El diseno
 *   Gamba son grupos planos de titulo + meta. Renderizar la seccion de Horizon
 *   para despues raspearle los links seria mas fragil que pedir el JSON, asi
 *   que se usa `/search/suggest.json` y se arma el DOM aca. Todo el texto que
 *   entra va por `textContent`, nunca por innerHTML.
 *
 * DOS FUENTES, UN SOLO RENDER
 * --------------------------
 * PRODUCTOS y PAGINAS salen de `/search/suggest.json`. RECETAS **no pueden**:
 * predictive search NO INDEXA METAOBJECTS, y la receta es el metaobject
 * `receta`. Asi que el grupo de recetas se matchea del lado del cliente sobre
 * el indice que `snippets/gamba-search-panel.liquid` emite en un
 * `<script type="application/json" data-search-recipes>`.
 *
 * El match local se resuelve DENTRO del mismo `#render` que los grupos
 * remotos, y no antes: un render por fuente daria dos repintados por tecleo,
 * dos `#retrap()` y un conteo para el anuncio de aria-live que no incluiria
 * las recetas. Un solo render, un solo anuncio.
 *
 * NORMALIZACION EN LOS DOS LADOS (la consulta y el indice): NFD + strip de
 * diacriticos + lowercase. Asi "burrata" matchea "Burrata" y "avocado"
 * matchea "Avocádo" si manana alguien lo carga con tilde. Liquid no puede
 * normalizar —no tiene NFD—, asi que el indice viaja crudo y se normaliza una
 * sola vez aca, la primera vez que se busca.
 */

/** Espera antes de disparar la request. */
const DEBOUNCE_MS = 220;

/** Milisegundos por letra del globo de Oli. Es el valor del boceto. */
const TYPE_MS = 38;

/**
 * Resultados por grupo. En los remotos lo aplica el endpoint
 * (`limit_scope=each`); en el de recetas lo aplica el `slice` del matching, con
 * el mismo numero para que los tres grupos midan igual.
 */
const RESOURCE_LIMIT = 4;

/** Formatos de producto, para la meta de la fila de resultado. */
const FORMATOS = ['formato', 'pack'];

/**
 * Primer tag del item que este en `vocabulario`, con el case de la lista.
 * Devuelve '' si no hay ninguno: la meta es opcional y la fila queda con el
 * titulo solo en vez de romperse.
 *
 * Se busca por lista canonica y NO por `tags[0]`: Shopify devuelve los tags
 * ordenados alfabeticamente, no en el orden en que se cargaron, asi que el
 * primer tag de `squeeze-500-ml-pet` es `500ml`.
 *
 * No se usa `product_type` para el formato aunque el boceto muestre
 * "Formato / Formato / Pack": ese campo es taxonomia de catalogo (alimenta
 * smart collections, analytics y los feeds de Google/Meta), y usarlo de label
 * mete un dato incorrecto que recien se nota cuando ya salio indexado.
 *
 * @param {any} item
 * @param {string[]} vocabulario
 * @returns {string}
 */
function metaPorTag(item, vocabulario) {
  const tags = item?.tags;
  if (!Array.isArray(tags)) return '';

  for (const termino of vocabulario) {
    const encontrado = tags.find((tag) => String(tag).toUpperCase() === termino.toUpperCase());
    if (encontrado) return termino.charAt(0).toUpperCase() + termino.slice(1).toLowerCase();
  }

  return '';
}

/**
 * NFD + strip de diacriticos + lowercase. Corre sobre los DOS lados —la
 * consulta y el indice— o el match seria asimetrico.
 *
 * El rango `\u0300-\u036f` es el bloque de diacriticos combinantes que deja
 * NFD despues de descomponer: "Avocádo" -> "avocado", "Pizza" -> "pizza".
 *
 * @param {unknown} texto
 * @returns {string}
 */
function normalizar(texto) {
  return String(texto ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/**
 * Los tres grupos de resultado del boceto, EN ORDEN, y de donde sale cada uno.
 *
 * `source: 'remote'` = /search/suggest.json. `source: 'local'` = el indice de
 * recetas que emite el snippet, porque predictive search no indexa
 * metaobjects (ver el encabezado del archivo). El orden del array ES el orden
 * en pantalla, asi que el grupo local queda en medio de los dos remotos.
 *
 * La meta es OPCIONAL en los tres: si el campo no viene, la fila queda con el
 * titulo solo en vez de romperse.
 *
 * OJO con `type` vs `resource` en los remotos: `resources[type]` de
 * /search/suggest.json se pide en SINGULAR (`product,page`) y la respuesta
 * vuelve indexada en PLURAL (`resources.results.products`). No son la misma
 * cadena.
 *
 * @type {{ source: 'remote' | 'local', type?: string, resource?: string, labelKey: string, meta: (item: any) => string }[]}
 */
const GROUPS = [
  {
    source: 'remote',
    type: 'product',
    resource: 'products',
    labelKey: 'labelProducts',
    meta: (item) => metaPorTag(item, FORMATOS),
  },
  { source: 'local', labelKey: 'labelRecipes', meta: (item) => item?.meta ?? '' },
  { source: 'remote', type: 'page', resource: 'pages', labelKey: 'labelPages', meta: () => '' },
];

/** Los que se le piden a /search/suggest.json. */
const REMOTE_GROUPS = GROUPS.filter((group) => group.source === 'remote');

const DESKTOP = matchMedia('(min-width: 990px)');

class GambaSearchPanel extends HTMLElement {
  /** @type {AbortController | null} */
  #fetching = null;

  /** @type {number | undefined} */
  #typeTimer;

  /** Texto completo del globo, leido una vez del DOM (fallback sin JS). */
  #tipText = '';

  /**
   * El indice de recetas: parseado, normalizado y cacheado la primera vez que
   * se busca. Lo emite el snippet y no cambia despues del render del servidor,
   * asi que se hace una vez y no en cada tecleo.
   *
   * @type {{ title: string, url: string, meta: string, haystack: string }[] | null}
   */
  #recipeIndex = null;

  #open = false;

  connectedCallback() {
    this.panel = this.querySelector('#gamba-search-panel');
    this.input = this.querySelector('[data-search-input]');
    this.idle = this.querySelector('[data-search-idle]');
    this.results = this.querySelector('[data-search-results]');
    this.empty = this.querySelector('[data-search-empty]');
    this.status = this.querySelector('[data-search-status]');
    this.tip = this.querySelector('[data-search-tip]');
    this.caret = this.querySelector('[data-search-caret]');
    this.toggle = document.querySelector('[data-search-toggle]');

    if (!this.panel || !this.input) return;

    this.#tipText = this.tip?.textContent?.trim() ?? '';

    this.toggle?.addEventListener('click', this.#onToggleClick);
    this.querySelector('[data-search-close]')?.addEventListener('click', () => this.close());
    this.input.addEventListener('input', this.#onInput);

    this.#syncPlaceholder();
    DESKTOP.addEventListener('change', this.#syncPlaceholder);
  }

  disconnectedCallback() {
    this.close({ restoreFocus: false });
    this.toggle?.removeEventListener('click', this.#onToggleClick);
    DESKTOP.removeEventListener('change', this.#syncPlaceholder);
  }

  /* ---------------------------------------------------------------- abrir */

  open() {
    if (this.#open || !this.panel) return;
    this.#open = true;

    this.panel.hidden = false;
    this.toggle?.setAttribute('aria-expanded', 'true');
    this.#show('idle');
    this.#typeTip();

    // `trapFocus` deja el foco en el contenedor; el input es lo que queremos.
    // Su handler de focusin solo redirige lo que cae AFUERA del panel, asi que
    // mover el foco adentro despues no se pelea con la trampa.
    this.#retrap();
    this.input?.focus();

    document.addEventListener('keydown', this.#onKeydown);
    // pointerdown y no click: un arrastre que empieza adentro del panel y
    // termina afuera generaria un `click` con target afuera y cerraria solo.
    document.addEventListener('pointerdown', this.#onPointerDown);
  }

  /**
   * @param {{ restoreFocus?: boolean }} [options]
   */
  close({ restoreFocus = true } = {}) {
    if (!this.#open || !this.panel) return;
    this.#open = false;

    // Devolver el foco SOLO si estaba adentro: si el panel se cierra por un
    // click afuera, el foco ya se lo llevo otra cosa y robarselo es peor.
    const focusWasInside = this.panel.contains(document.activeElement);

    this.#onInput.cancel();
    this.#abort();
    clearInterval(this.#typeTimer);
    removeTrapFocus();
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('pointerdown', this.#onPointerDown);

    this.panel.hidden = true;
    this.toggle?.setAttribute('aria-expanded', 'false');
    if (this.input) this.input.value = '';
    this.results?.replaceChildren();
    if (this.status) this.status.textContent = '';
    this.#show('idle');

    if (restoreFocus && focusWasInside) this.toggle?.focus();
  }

  #onToggleClick = () => {
    if (this.#open) this.close();
    else this.open();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  };

  /**
   * Rearma la trampa de foco.
   *
   * `trapFocus` congela el primer y el ultimo focusable EN EL MOMENTO en que
   * se llama, asi que armarla una sola vez al abrir dejaria afuera los links
   * de resultado, que se crean despues: el Tab hacia adelante no cerraria el
   * ciclo y el Shift+Tab desde el input intentaria saltar a un atajo del
   * estado idle que ya esta `hidden`. El foco nunca ESCAPA —de eso se ocupa el
   * handler de focusin— pero el ciclo queda cojo. Rearmarla en cada cambio de
   * estado lo arregla.
   *
   * `trapFocus` termina con `container.focus()`, o sea que se lleva el foco del
   * input; hay que devolverlo Y restaurar el caret, porque esto corre mientras
   * la persona esta tecleando. `type="search"` soporta setSelectionRange.
   */
  #retrap() {
    if (!this.panel) return;

    const { input } = this;
    const hadFocus = document.activeElement === input;
    const start = input?.selectionStart ?? null;
    const end = input?.selectionEnd ?? null;

    trapFocus(this.panel);

    if (!hadFocus || !input) return;
    input.focus();
    if (start !== null && end !== null) input.setSelectionRange(start, end);
  }

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (!this.panel) return;
    // El propio boton de la lupa cuenta como "adentro": si no, el pointerdown
    // cerraria y el click posterior volveria a abrir.
    if (this.toggle && event.target instanceof Node && this.toggle.contains(event.target)) return;
    if (isClickedOutside(event, this.panel)) this.close();
  };

  /* ----------------------------------------------------------- placeholder */

  // Escucha el `change` del media query y no `resize`: `resize` dispara en
  // cada pixel y en mobile tambien cuando se abre el teclado virtual.
  #syncPlaceholder = () => {
    const { input } = this;
    if (!input) return;
    const next = DESKTOP.matches
      ? input.dataset.placeholderDesktop
      : input.dataset.placeholderMobile;
    if (next) input.placeholder = next;
  };

  /* ------------------------------------------------------- globo de Oli */

  /**
   * Escribe la frase letra por letra. El span visible ya trae la frase
   * completa desde Liquid (fallback sin JS), asi que con reduced-motion no hay
   * nada que hacer mas que asegurarla y esconder el caret.
   *
   * El span visible lleva aria-hidden y hay un hermano visually-hidden con la
   * frase estatica, asi que el lector de pantalla la escucha entera una sola
   * vez y NO letra por letra. Ver el snippet.
   */
  #typeTip() {
    const { tip } = this;
    if (!tip || !this.#tipText) return;

    clearInterval(this.#typeTimer);

    if (prefersReducedMotion()) {
      tip.textContent = this.#tipText;
      if (this.caret) this.caret.hidden = true;
      return;
    }

    let index = 0;
    tip.textContent = '';
    if (this.caret) this.caret.hidden = false;

    this.#typeTimer = setInterval(() => {
      index += 1;
      tip.textContent = this.#tipText.slice(0, index);
      if (index < this.#tipText.length) return;
      clearInterval(this.#typeTimer);
      if (this.caret) this.caret.hidden = true;
    }, TYPE_MS);
  }

  /* ----------------------------------------------------------- busqueda */

  #onInput = debounce(() => {
    const query = this.input?.value.trim() ?? '';

    if (!query) {
      this.#abort();
      if (this.status) this.status.textContent = '';
      this.#show('idle');
      this.#retrap();
      return;
    }

    this.#search(query);
  }, DEBOUNCE_MS);

  #abort() {
    this.#fetching?.abort();
    this.#fetching = null;
  }

  /**
   * @param {string} query
   */
  async #search(query) {
    // Cancela la request anterior: una respuesta lenta que llega despues de
    // una mas nueva no puede sobreescribir los resultados. El abort corta el
    // fetch, y el chequeo de `signal.aborted` de abajo cubre la carrera entre
    // el `abort()` y un `await` que ya estaba resuelto.
    this.#abort();
    const controller = new AbortController();
    this.#fetching = controller;

    const base = this.dataset.suggestUrl || `${window.Shopify?.routes?.root ?? '/'}search/suggest`;
    const url = new URL(base, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', REMOTE_GROUPS.map((group) => group.type).join(','));
    url.searchParams.set('resources[limit]', String(RESOURCE_LIMIT));
    url.searchParams.set('resources[limit_scope]', 'each');

    try {
      const response = await fetch(`${url.pathname}.json${url.search}`, { signal: controller.signal });
      if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);

      const payload = await response.json();
      if (controller.signal.aborted) return;

      const total = this.#render(payload?.resources?.results ?? {}, query);

      if (total === 0) {
        this.#show('empty');
        this.#retrap();
        this.#announce(this.dataset.announceEmpty, { query });
        return;
      }

      this.#show('results');
      this.#retrap();
      this.#announce(this.dataset.announceResults, { query, count: total });
    } catch (error) {
      if (controller.signal.aborted || (error instanceof DOMException && error.name === 'AbortError')) return;

      // Una busqueda que falla no puede dejar la nav rota. Se vuelve a idle y
      // el submit del form (Enter) sigue llevando a /search, que es server
      // side y no depende de esto.
      console.error('[gamba-search]', error);
      this.#show('idle');
      this.#retrap();
      if (this.status) this.status.textContent = '';
    } finally {
      if (this.#fetching === controller) this.#fetching = null;
    }
  }

  /**
   * El indice de recetas, parseado y normalizado. Se cachea.
   *
   * Un JSON invalido, un array vacio o un nodo que no esta dan lo mismo: el
   * grupo de recetas no aparece y los otros dos siguen funcionando. Una receta
   * sin titulo o sin URL se descarta aca tambien, aunque el snippet ya no la
   * emita: el indice es dato de entrada y se valida.
   *
   * @returns {{ title: string, url: string, meta: string, haystack: string }[]}
   */
  #recipes() {
    if (this.#recipeIndex) return this.#recipeIndex;

    let raw;
    try {
      raw = JSON.parse(this.querySelector('[data-search-recipes]')?.textContent || '[]');
    } catch (error) {
      console.error('[gamba-search]', error);
      raw = [];
    }

    this.#recipeIndex = (Array.isArray(raw) ? raw : []).flatMap((entry) => {
      const title = typeof entry?.titulo === 'string' ? entry.titulo.trim() : '';
      const url = typeof entry?.url === 'string' ? entry.url : '';
      if (!title || !url) return [];

      const momentos = Array.isArray(entry?.momentos) ? entry.momentos.map(String) : [];
      const terminos = typeof entry?.terminos === 'string' ? entry.terminos : '';

      // El PRIMERO de la lista, que el Liquid ya emitio en orden CANONICO: es
      // el mismo criterio con el que la card de la landing elige su chip, asi
      // que la etiqueta no depende de en que orden se tipeo el campo.
      const primero = momentos[0] ?? '';

      return [
        {
          title,
          url,
          meta: primero ? primero.charAt(0).toUpperCase() + primero.slice(1).toLowerCase() : '',
          // `momento` y `terminos_busqueda` entran al mismo haystack que el
          // titulo, y `terminos_busqueda` es el que hace que "burrata" o
          // "palta" acierten una receta que no los tiene en el titulo. Es el
          // campo que existe para esto.
          haystack: normalizar([title, momentos.join(' '), terminos].join(' ')),
        },
      ];
    });

    return this.#recipeIndex;
  }

  /**
   * Recetas que matchean la consulta, hasta `RESOURCE_LIMIT` como los remotos.
   *
   * SIN FUZZY: es `includes` sobre el haystack normalizado, cero distancia de
   * edicion. La consulta se parte en palabras y se piden TODAS, que sigue
   * siendo substring exacto pero deja que "burrata tomate" encuentre la receta
   * que tiene una palabra en el titulo y la otra en los terminos. Con una sola
   * palabra las dos formas son identicas.
   *
   * @param {string} query
   * @returns {{ title: string, url: string, meta: string }[]}
   */
  #matchRecipes(query) {
    const terms = normalizar(query).split(/\s+/).filter(Boolean);
    if (terms.length === 0) return [];

    return this.#recipes()
      .filter((recipe) => terms.every((term) => recipe.haystack.includes(term)))
      .slice(0, RESOURCE_LIMIT);
  }

  /**
   * Arma el DOM de resultados y devuelve cuantos items quedaron.
   *
   * @param {Record<string, unknown>} results  lo que devolvio /search/suggest.json
   * @param {string} query  la consulta cruda, para el matching local
   * @returns {number}
   */
  #render(results, query) {
    const fragment = document.createDocumentFragment();
    let total = 0;

    for (const group of GROUPS) {
      const items = group.source === 'local' ? this.#matchRecipes(query) : results[group.resource ?? ''];
      if (!Array.isArray(items) || items.length === 0) continue;

      const list = document.createElement('div');
      list.className = 'gamba-search__group-items';

      for (const item of items) {
        if (!item?.url || !item?.title) continue;

        const link = document.createElement('a');
        link.className = 'gamba-search__result';
        link.href = item.url;

        const title = document.createElement('span');
        title.className = 'gamba-search__result-title';
        title.textContent = item.title;
        link.append(title);

        const metaText = group.meta(item);
        if (typeof metaText === 'string' && metaText.trim() !== '') {
          const meta = document.createElement('span');
          meta.className = 'gamba-search__result-meta';
          meta.textContent = metaText;
          link.append(meta);
        }

        list.append(link);
        total += 1;
      }

      if (list.childElementCount === 0) continue;

      const groupEl = document.createElement('div');
      groupEl.className = 'gamba-search__group';

      const label = document.createElement('p');
      label.className = 'gamba-search__group-title';
      label.textContent = this.dataset[group.labelKey] ?? '';

      groupEl.append(label, list);
      fragment.append(groupEl);
    }

    this.results?.replaceChildren(fragment);
    return total;
  }

  /**
   * Los dos textos vienen traducidos de Liquid con marcadores `%token%`, porque
   * el JS no puede resolver `| t`.
   *
   * @param {string | undefined} template
   * @param {Record<string, string | number>} values
   */
  #announce(template, values) {
    if (!this.status || !template) return;
    this.status.textContent = Object.entries(values).reduce(
      (text, [token, value]) => text.replaceAll(`%${token}%`, String(value)),
      template
    );
  }

  /**
   * @param {'idle' | 'results' | 'empty'} state
   */
  #show(state) {
    if (this.idle) this.idle.hidden = state !== 'idle';
    if (this.results) this.results.hidden = state !== 'results';
    if (this.empty) this.empty.hidden = state !== 'empty';
  }
}

if (!customElements.get('gamba-search-panel')) {
  customElements.define('gamba-search-panel', GambaSearchPanel);
}
